/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import {
  SLRLoadingButton,
  SLRPrompt,
  SLRPromptLabels,
  showErrorToast
} from '@slr/shared-library';
import {
  useCreateDataPrivacyReport,
  useDeleteUserAccount,
  useGetAccountDeletionEligibility,
  useGetCurrentUserProfile
} from 'hooks';
import { FC, useCallback, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { CONTACT_PATH } from 'configs';
import './ProfileDataPrivacy.scss';

export const ProfileDataPrivacy: FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'profilePage.dataPrivacy'
  });

  const { data: user } = useGetCurrentUserProfile();

  const userId = useMemo(() => user?.id ?? '', [user?.id]);

  const [promptData, setPromptData] = useState<{
    labels: SLRPromptLabels;
    confirmButtonVariant?: 'danger';
    onConfirm: () => void;
  } | null>(null);

  const hidePrompt = useCallback(() => setPromptData(null), []);

  const {
    mutate: requestDataPrivacyReport,
    isPending: isDataPrivacyReportLoading
  } = useCreateDataPrivacyReport();
  const { mutate: deleteUserAccount, isPending: isDeleteAccountLoading } =
    useDeleteUserAccount();
  const { refetch: refetchDeletionEligibility } =
    useGetAccountDeletionEligibility(userId, false);

  const handleRequestDataReport = useCallback(
    () => requestDataPrivacyReport({ userId }),
    [requestDataPrivacyReport, userId]
  );

  const handleDeleteUserAccount = useCallback(() => {
    if (!userId) {
      return;
    }

    void refetchDeletionEligibility().then((result) => {
      if (result.isError || !result.data) {
        showErrorToast('toasts.unexpected.error');
        return;
      }

      const deletionEligibility = result.data;

      if (!deletionEligibility.canDelete) {
        if (deletionEligibility.blockedAsLastPlatformAdministrator) {
          setPromptData({
            labels: {
              title: t('deleteBlockedLastPlatformAdmin.title'),
              content: t('deleteBlockedLastPlatformAdmin.content'),
              cancel: t('deleteBlockedLastPlatformAdmin.cancel'),
              confirm: t('deleteBlockedLastPlatformAdmin.confirm')
            },
            onConfirm: hidePrompt
          });
          return;
        }

        setPromptData({
          labels: {
            title: t('deleteBlocked.title'),
            content: t('deleteBlocked.content', {
              offerCount: deletionEligibility.offerCount,
              bookingRequestCount: deletionEligibility.bookingRequestCount,
              bookingCount: deletionEligibility.bookingCount
            }),
            cancel: t('deleteBlocked.cancel'),
            confirm: t('deleteBlocked.confirm')
          },
          onConfirm: hidePrompt
        });
        return;
      }

      setPromptData({
        confirmButtonVariant: 'danger',
        labels: t('deleteConfirmation', {
          returnObjects: true
        }) as SLRPromptLabels,
        onConfirm: () => {
          deleteUserAccount(
            { userId },
            { onSettled: hidePrompt }
          );
        }
      });
    });
  }, [
    deleteUserAccount,
    hidePrompt,
    refetchDeletionEligibility,
    t,
    userId
  ]);

  return (
    <>
      {promptData && (
        <SLRPrompt
          isLoading={isDeleteAccountLoading}
          labels={promptData.labels}
          confirmButtonVariant={promptData.confirmButtonVariant}
          onConfirm={promptData.onConfirm}
          onCancel={hidePrompt}
        />
      )}
      <div className="profile-data-privacy">
        <h1>{t('title')}</h1>

        <div className="mt-5">
          <h2>{t('requestReport')}</h2>

          <p className="mt-3 mb-4 description">
            <Trans t={t} i18nKey="requestDescription" />
          </p>

          <SLRLoadingButton
            isLoading={isDataPrivacyReportLoading}
            width="16rem"
            onClick={handleRequestDataReport}
          >
            {t('requestReport')}
          </SLRLoadingButton>
        </div>

        <div className="mt-5">
          <h2>{t('deleteAccount')}</h2>

          <p className="mt-3 mb-4 description">
            <Trans
              t={t}
              i18nKey="deleteDescription"
              components={[<Link to={CONTACT_PATH} key={CONTACT_PATH} />]}
            />
          </p>
          <Button
            size="lg"
            onClick={handleDeleteUserAccount}
            variant="outline-danger"
          >
            {t('deleteAccount')}
          </Button>
        </div>
      </div>
    </>
  );
};
